﻿import React, { useState } from "react";
import useAsync from "../../utils/useAsync.js";
import { getValuationData } from "../../utils/companyApi.js";
import { fmtCell, fmtMoney } from "../../utils/format.js";
import { stockPath } from "../../utils/route.js";
import { Loading, SubTabs } from "./common.jsx";

const ROWS = [
  ["pe", "P/E", "x"],
  ["ps", "Price / Sales", "x"],
  ["pb", "Price / Book", "x"],
  ["ev_ebitda", "EV / EBITDA", "x"],
  ["roce", "ROCE", "pct"],
  ["nim", "Net margin", "pct"],
  ["sales_g", "Sales growth (LTM)", "pct"],
  ["div_yield", "Dividend yield", "pct"],
];

export default function ValuationTab({ ticker }) {
  const [view, setView] = useState("multiples");
  const { data, loading, error } = useAsync(() => getValuationData(ticker), [ticker]);

  if (loading) return <Loading />;
  if (error || !data || data.error) {
    return <div className="card co-card"><p className="co-hint">No valuation data for this company yet. It needs a share price and SEC financials.</p></div>;
  }
  const label = data.group ? `${data.n.toLocaleString("en-US")} companies in ${data.groupType === "industry" ? "the" : "the"} ${data.group} ${data.groupType}` : "";

  return (
    <div>
      <SubTabs
        items={[["multiples", "Multiples"], ["competitors", "Competitors"], ["targets", "Street Targets"]]}
        value={view}
        onChange={setView}
      />

      {view === "multiples" && (
        <div className="card co-card co-table-wrap">
          <table className="co-table co-table-left">
            <thead>
              <tr><th>Measure</th><th>{ticker}</th><th>Industry median</th></tr>
            </thead>
            <tbody>
              {ROWS.map(([k, name, kind]) => (
                <tr key={k}>
                  <td>{name}</td>
                  <td>{fmtCell(data.self[k], kind)}</td>
                  <td>{fmtCell(data.medians[k], kind)}</td>
                </tr>
              ))}
              <tr>
                <td>P/E on 10-year average profit</td>
                <td>{fmtCell(data.self.pe_avg10, "x")}</td>
                <td>—</td>
              </tr>
            </tbody>
          </table>
          <p className="co-hint">{label ? `Median of ${label}. ` : ""}Calculated from SEC financials and your latest price. Financial companies have different accounting, so compare them with each other.</p>
        </div>
      )}

      {view === "competitors" && (
        <div className="card co-card co-table-wrap">
          <table className="co-table co-table-left">
            <thead>
              <tr><th>Company</th><th>Price</th><th>Mkt Cap ($B)</th><th>P/E</th><th>Sales Growth</th><th>Net Margin</th><th>ROCE</th></tr>
            </thead>
            <tbody>
              {[{ ...data.self, self: true }, ...data.peers].map((c) => (
                <tr key={c.ticker} className={c.self ? "selected" : ""}>
                  <td>
                    <a className="wl-company" href={stockPath(c.ticker)}>{c.name}</a>
                    <small className="co-tk">{c.ticker}</small>
                  </td>
                  <td>{fmtMoney(c.price)}</td>
                  <td>{fmtCell(c.mcap, "count")}</td>
                  <td>{fmtCell(c.pe, "x")}</td>
                  <td>{fmtCell(c.sales_g, "pct")}</td>
                  <td>{fmtCell(c.nim, "pct")}</td>
                  <td>{fmtCell(c.roce, "pct")}</td>
                </tr>
              ))}
              {!data.peers.length && <tr><td colSpan={7} className="co-hint">No peers found.</td></tr>}
            </tbody>
          </table>
          <p className="co-hint">The ten companies closest in size within the same {data.groupType}.</p>
        </div>
      )}

      {view === "targets" && (
        <div className="card co-card">
          <p className="co-hint">
            Analyst price targets and ratings come from brokerage research, which data vendors sell under licence.
            There is no free source, so this view stays empty until a data feed is connected.
          </p>
        </div>
      )}
    </div>
  );
}

